import React, { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '../../../ui';
import { Icon } from '../../../ui/Icon';
import PhoneInputField from '../../../common/PhoneInputField';
import SmartLocationFields from './SmartLocationFields';
import { AddressFormSectionProps, AddressFormData } from './types';
import { isValidPhoneNumber } from './utils';

const AddressFormSection = React.memo<AddressFormSectionProps>(
  ({
    title,
    type,
    form,
    onFormChange,
    onAddressBookOpen,
    // onGooglePlaceSelect — reserved for SmartLocationFields integration
    isFormValid,
    allowedOriginCountryId,
    allowedDestinationCountryId,
    allCountries,
    onNextStep,
    onPostalCodeError,
  }) => {
    const { control, setValue, trigger } = useForm<AddressFormData>({
      defaultValues: form,
      mode: 'onChange',
    });

    const selectedCountry = React.useMemo(
      () => allCountries.find((c) => c.id === form.country_id),
      [allCountries, form.country_id]
    );

    React.useEffect(() => {
      Object.keys(form).forEach((key) => {
        const k = key as keyof AddressFormData;
        setValue(k, form[k]);
      });
    }, [form, setValue]);

    React.useEffect(() => {
      if (form.phone && form.phone.trim() !== '') {
        setTimeout(() => trigger('phone'), 150);
      }
    }, [form.country_id, trigger, form.phone]);

    const getErrorMessage = useCallback(() => {
      const hasCity = Boolean(form.city_other && form.city_other.trim().length > 0);
      if (form.contactName && form.street && form.zipCode && form.country_id && hasCity) {
        const requiredId = type === 'source' ? allowedOriginCountryId : allowedDestinationCountryId;
        if (requiredId && form.country_id !== requiredId) {
          const requiredName = allCountries.find((c) => c.id === requiredId)?.name;
          return `Wrong country — required: ${requiredName || 'Unknown'}`;
        }
      }
      return 'Please complete required fields';
    }, [form.contactName, form.street, form.zipCode, form.country_id, form.city_other, type, allowedOriginCountryId, allowedDestinationCountryId, allCountries]);

    return (
      <div>
        {/* Header row — only rendered when a title prop is passed.
            When AddressSelection uses numbered ship-row headers, title is '' and this is skipped. */}
        {title ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 18,
              gap: 12,
            }}
          >
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>{title}</h3>
              <p className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                {type === 'source'
                  ? 'Where the carrier will collect the parcel.'
                  : 'Where the parcel should arrive.'}
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={onAddressBookOpen} title="Choose from saved contacts">
              <Icon name="users" size={13} />
              <span>Address book</span>
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
            <Button size="sm" variant="ghost" onClick={onAddressBookOpen} title="Choose from saved contacts">
              <Icon name="users" size={13} />
              <span>Address book</span>
            </Button>
          </div>
        )}

        {/* Contact block */}
        <div className="field-label" style={{ marginBottom: 10 }}>
          Contact information
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="field">
            <label className="field-label">
              Contact name <span style={{ color: 'var(--err)' }}>*</span>
            </label>
            <input
              type="text"
              className="input"
              value={form.contactName}
              onChange={(e) => onFormChange('contactName', e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label className="field-label">Company</label>
            <input
              type="text"
              className="input"
              value={form.companyName}
              onChange={(e) => onFormChange('companyName', e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label">Email</label>
            <input
              type="email"
              className="input"
              value={form.email}
              onChange={(e) => onFormChange('email', e.target.value)}
            />
          </div>
          <div className="field">
            <Controller
              name="phone"
              control={control}
              rules={{
                validate: (value: string) => {
                  if (!value || value.trim() === '') return true;
                  const countryCode = selectedCountry?.iso2 || 'US';
                  return isValidPhoneNumber(value, countryCode) || 'Please enter a valid phone number';
                },
              }}
              render={({ field: { onChange, onBlur }, fieldState: { error } }) => (
                <PhoneInputField
                  key={`phone-${selectedCountry?.iso2 || 'il'}`}
                  name="phone"
                  control={control}
                  label="Phone number"
                  placeholder="Enter phone number"
                  country={selectedCountry?.iso2?.toLowerCase() || 'il'}
                  preferredCountries={['us', 'gb', 'ca', 'au', 'il', 'ps']}
                  enableSearch
                  autoFormat={false}
                  helperText={error?.message}
                  onPhoneChange={(phoneValue: string) => {
                    onFormChange('phone', phoneValue);
                    onChange(phoneValue);
                    setTimeout(() => {
                      if (phoneValue && phoneValue.trim() !== '') {
                        setTimeout(() => trigger('phone'), 100);
                      }
                    }, 150);
                  }}
                  onPhoneBlur={onBlur}
                  disableCountrySelect
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </div>
        </div>

        <div className="divider" style={{ margin: '20px 0 14px' }} />

        {/* Address block */}
        <div className="field-label" style={{ marginBottom: 10 }}>
          Address
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
          <div className="field">
            <label className="field-label">
              Street <span style={{ color: 'var(--err)' }}>*</span>
            </label>
            <input
              type="text"
              className="input"
              value={form.street}
              onChange={(e) => onFormChange('street', e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label className="field-label">House / unit</label>
            <input
              type="text"
              className="input"
              value={form.houseNo}
              onChange={(e) => onFormChange('houseNo', e.target.value)}
            />
          </div>
        </div>

        <SmartLocationFields
          form={form}
          onFormChange={onFormChange}
          allowedCountryId={
            type === 'source' ? allowedOriginCountryId : allowedDestinationCountryId
          }
          isDestination={type === 'destination'}
          onPostalCodeError={onPostalCodeError}
        />

        <div className="field" style={{ marginTop: 14 }}>
          <label className="field-label">Delivery instructions</label>
          <textarea
            className="textarea"
            value={form.deliveryDetails}
            onChange={(e) => onFormChange('deliveryDetails', e.target.value)}
            placeholder="Gate code, floor, preferred drop-off spot…"
            rows={2}
          />
        </div>

        {/* Status row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            marginTop: 16,
          }}
        >
          {isFormValid ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 99,
                background: 'var(--ok-bg)',
                color: 'var(--ok)',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <Icon name="check" size={12} /> Address complete
            </span>
          ) : (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 99,
                background: 'var(--warn-bg)',
                color: 'var(--warn)',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <Icon name="edit" size={12} /> {getErrorMessage()}
            </span>
          )}

          {isFormValid && onNextStep && type === 'source' && (
            <Button size="sm" variant="blue" onClick={onNextStep}>
              <span>Next: delivery address</span>
              <Icon name="arrowRight" size={14} />
            </Button>
          )}
        </div>
      </div>
    );
  }
);

AddressFormSection.displayName = 'AddressFormSection';

export default AddressFormSection;
